import { Link } from "react-router-dom";
import PublicNav from "../components/PublicNav";
import PublicFooter from "../components/PublicFooter";
import PublicBottomNav from "../components/PublicBottomNav";
import { Helmet } from "react-helmet-async";

import JsonLd from "../components/JsonLd";
import { JobPost, JobGlance, JOB_TITLE } from "../components/careers/JobPost";
import { ApplicationForm } from "../components/careers/ApplicationForm";
import { pic } from "../lib/pic";

const LOGO = "https://customer-assets.emergentagent.com/job_184a0a73-7ed5-43f8-9a23-0cd69b83d3bb/artifacts/rcargp5m_no%20bkg.png";
const HERO = "https://customer-assets.emergentagent.com/job_1f43f610-aff8-4e7c-82fd-ea58f8ef0225/artifacts/fan8mv98_708A1538.jpg";

const jobPosting = {
  "@context": "https://schema.org",
  "@type": "JobPosting",
  title: JOB_TITLE,
  description:
    "Full-time, paid, live-in role running the house, the volunteer crew and the community at Savi Coliving, a sober coliving in the Sierra Calderona, 25 minutes from Valencia. Own room and all food included, plus a salary. Paid three-month trial, then a long-term role with a path to partnership as the brand grows.",
  datePosted: "2026-10-17",
  validThrough: "2026-12-31T23:59:59+01:00",
  employmentType: "FULL_TIME",
  directApply: true,
  hiringOrganization: { "@type": "Organization", name: "Savi Coliving", sameAs: "https://www.savicoliving.com", logo: LOGO },
  jobLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Sierra Calderona, Náquera",
      addressLocality: "Náquera",
      postalCode: "46119",
      addressRegion: "Valencia",
      addressCountry: "ES",
    },
  },
  jobBenefits: "Own room and all food included",
  incentiveCompensation: "Salary by agreement, plus own room and all food. Paid three-month trial with a path to partnership.",
};


export default function CareersPage() {
  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <Helmet>
        <title>Careers | Chief of Everything That Isn't Bookings | Savi Coliving</title>
        <meta name="description" content="Savi Coliving is hiring a full-time, paid, live-in Chief of Everything That Isn't Bookings. Own room and food included, plus a salary. Sierra Calderona, 25 minutes from Valencia." />
        <meta property="og:title" content="Wanted: Chief of Everything That Isn't Bookings" />
        <meta property="og:description" content="Full time, paid, immediate start. Your own room and all food included, plus a salary. Savi Coliving, Sierra Calderona, Valencia." />
        <meta property="og:image" content={HERO} />
        <link rel="canonical" href="https://www.savicoliving.com/careers" />
      </Helmet>
      <JsonLd id="job-posting" data={jobPosting} />

      <PublicNav />

      <PublicBottomNav />

      <header className="pt-16">
        <div className="h-[45vh] sm:h-[55vh] relative overflow-hidden">
          <img {...pic("house-team")} fetchpriority="high" decoding="async" alt="The pool terrace under the brick arches at Savi Coliving, Sierra Calderona behind" className="w-full h-full object-cover object-center" data-testid="careers-hero-photo" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#FBF7F1] from-20% via-[#FBF7F1]/80 via-50% to-black/20"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
            <div className="max-w-6xl mx-auto">
              <p className="text-[#C85A38] font-medium text-sm sm:text-base mb-1">We're hiring</p>
              <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#2E2A26] mb-2" data-testid="careers-title">
                Wanted: {JOB_TITLE}
              </h1>
              <p className="text-[#2E2A26]/70 text-sm sm:text-base max-w-2xl">
                Savi Coliving, Sierra Calderona, Valencia. Full time, paid, immediate start. Your own room and all food included, plus a salary.
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="pb-24 md:pb-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="grid lg:grid-cols-[1fr_300px] gap-10 lg:gap-14 items-start">
            <JobPost />
            <div className="lg:sticky lg:top-24 flex flex-col gap-4">
              <JobGlance />
              <a href="#apply" className="block text-center bg-[#C85A38] hover:bg-[#D06A4F] text-white rounded-full px-6 py-3 text-sm font-medium transition-colors" data-testid="careers-skip-to-form">
                Go to the form
              </a>
            </div>
          </div>
        </div>

        <section id="apply" className="bg-[#FBF7F1] border-t border-stone-100 scroll-mt-20" data-testid="apply-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
            <p className="text-[#C85A38] font-medium text-sm sm:text-base mb-2">Apply</p>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#2E2A26] mb-3">The form</h2>
            <p className="text-sm sm:text-base text-[#2E2A26]/70 leading-relaxed mb-8">
              Twenty-two questions. Nine are required and marked with a <span className="text-[#C85A38]">*</span>, including a short video. The rest are there because they're useful, not because we're nosy.
            </p>
            <ApplicationForm />
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
